import { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { authApi } from '@/api/auth';
import { Button } from '@/components/ui/Button';
import { useTranslation } from '@/hooks/useTranslation';
import logo from '@/assets/images/logo-sidebar.png';

export function EmailVerificationPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [errorMessage, setErrorMessage] = useState('');
  const verificationAttempted = useRef(false);

  useEffect(() => {
    // Prevent double verification (React StrictMode causes double render in dev)
    if (verificationAttempted.current) {
      console.log('[EmailVerification] Already attempted, skipping');
      return;
    }

    const token = searchParams.get('token');
    console.log('[EmailVerification] Token from URL:', token);

    if (!token) {
      console.log('[EmailVerification] No token found');
      setStatus('error');
      setErrorMessage(t('auth.emailVerification.tokenMissing'));
      return;
    }

    console.log('[EmailVerification] Starting verification...');
    verificationAttempted.current = true;
    verifyEmail(token);
  }, [searchParams, t]);

  const verifyEmail = async (token: string) => {
    console.log('[EmailVerification] Calling API with token:', token);
    try {
      await authApi.verifyEmail(token);
      console.log('[EmailVerification] API call successful');
      setStatus('success');
    } catch (error: any) {
      console.error('[EmailVerification] API call failed:', error);
      console.error('[EmailVerification] Error message:', error.message);
      
      // Check if it's a token expired with new email sent (special case)
      if (error.response?.data?.error === 'TOKEN_EXPIRED_NEW_SENT') {
        setStatus('error');
        setErrorMessage(t('auth.emailVerification.tokenExpiredNewSent'));
        return;
      }
      
      setStatus('error');
      
      // Map specific error messages to translations
      const errorMsg = error.message || '';
      
      // Check for Portuguese messages from backend
      if (errorMsg.includes('novo email') || errorMsg.includes('new verification email')) {
        setErrorMessage(t('auth.emailVerification.tokenExpiredNewSent'));
      } else if (errorMsg.includes('inválido') || errorMsg.includes('já utilizado') || 
          errorMsg.includes('already used') || errorMsg.includes('Invalid or already used')) {
        setErrorMessage(t('auth.emailVerification.tokenInvalid'));
      } else if (errorMsg.includes('expirado') || errorMsg.includes('expired')) {
        setErrorMessage(t('auth.emailVerification.tokenExpired'));
      } else if (errorMsg.includes('já verificado') || errorMsg.includes('already verified')) {
        setErrorMessage(t('auth.emailVerification.alreadyVerified'));
      } else {
        // Display the backend message directly if it doesn't match any pattern
        setErrorMessage(errorMsg || t('auth.emailVerification.errorTitle'));
      }
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: '#F6F6F8' }}
    >
      <div 
        className="w-full"
        style={{
          maxWidth: '450px',
          backgroundColor: '#FFF',
          borderRadius: '8px',
          padding: '48px 40px',
          boxShadow: '0px 4px 8px -4px rgba(50, 71, 92, 0.42)'
        }}
      >
        {/* Logo e Título */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img 
              src={logo} 
              alt="FeedBatch Logo" 
              style={{ 
                height: '120px',
                width: 'auto'
              }}
            />
          </div>
          <h1 
            style={{ 
              fontSize: '24px',
              fontWeight: 600,
              color: '#22212C',
              marginBottom: '8px'
            }}
          >
            FeedBatch
          </h1>
        </div>

        <div className="text-center">
          {status === 'verifying' && (
            <>
              <div className="mx-auto h-12 w-12 text-primary-600">
                <svg
                  className="animate-spin h-12 w-12"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
              <h2 className="mt-6 text-2xl font-bold text-gray-900">
                {t('auth.emailVerification.verifying')}
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                {t('auth.emailVerification.verifyingMessage')}
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg
                  className="h-6 w-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="mt-6 text-2xl font-bold text-gray-900">
                {t('auth.emailVerification.successTitle')}
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                {t('auth.emailVerification.successMessage')}
              </p>
              <div className="mt-6">
                <Link to="/login">
                  <Button className="w-full">
                    {t('auth.emailVerification.goToSignIn')}
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h2 className="mt-6 text-2xl font-bold text-gray-900">
                {t('auth.emailVerification.errorTitle')}
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                {errorMessage}
              </p>
              <div className="mt-6 space-y-3">
                <Link to="/login">
                  <Button variant="outline" className="w-full">
                    {t('auth.emailVerification.goToSignIn')}
                  </Button>
                </Link>
                <p className="text-sm text-gray-500">
                  {t('auth.emailVerification.contactSupport')}{' '}
                  <a href="#" className="text-primary-600 hover:text-primary-500">
                    {t('auth.emailVerification.support')}
                  </a>
                </p>
              </div>
            </>
          )}
        </div>

        {/* Links de Privacidade e Termos */}
        <div className="text-center mt-8 flex justify-center gap-3 text-xs" style={{ color: 'rgba(50, 71, 92, 0.6)' }}>
          <a
            href="/politica-de-privacidade"
            target="_blank"
            rel="noopener noreferrer"
            style={{ 
              color: 'rgba(50, 71, 92, 0.6)',
              textDecoration: 'none'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--wl-accent)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(50, 71, 92, 0.6)'}
          >
            {t('auth.privacyPolicy')}
          </a>
          <span>•</span>
          <a
            href="/termos-de-servico"
            target="_blank"
            rel="noopener noreferrer"
            style={{ 
              color: 'rgba(50, 71, 92, 0.6)',
              textDecoration: 'none'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--wl-accent)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(50, 71, 92, 0.6)'}
          >
            {t('auth.termsOfService')}
          </a>
        </div>
      </div>
    </div>
  );
}
